import moment from 'moment'
import {Template, Component, Attribute} from '@scoutgg/ftw/source/lib'
import '@scoutgg/ftw/source/services/drag-and-derp'
import '@scoutgg/ftw/source/services/drag-with-image'
import notify from '@scoutgg/ftw/source/services/notification'
import sourceManager from '../../services/source-manager'
import query from '../../services/query'
import '@scoutgg/ftw/source/atoms/button/button'
import normalize from '../../../helpers/normalize'
import customFields from '../../../helpers/match-custom-fields'
import template from './match-sources.jade'
import Modal from '@scoutgg/ftw/source/services/modal'
import {Season, MatchSource, SeasonSource} from '../../models'
import EditMatchSource from '../edit-match-source'
import config from '@scoutgg/ftw/source/services/config'

import '../seasons'

function byTime(a, b) {
  return Date.parse(b.startTime) - Date.parse(a.startTime)
}
function customToKey(match) {
  if(!match) return
  let date = moment(match.startTime).format('LL').toString()
  if(!date) return
  if(!match.teams) return

  return (
      match.teams.length === 2 ?
      [match.teams[0].name, match.teams[1].name, date]
    : [
      moment(match.startTime).format('LLLL').toString()
    ]
  ).map(normalize).join('|')
}
const mapping = {
  binary: 'BinaryString',
  string: 'BinaryString',
  buffer: 'ArrayBuffer',
  text: 'Text',
  uri: 'DataURL',
}

function load(file, type) {
  type = mapping[type] || ''

  const reader = new FileReader()
  return new Promise((resolve, reject) => {
    reader.onload = e => resolve(e.target.result)
    reader.onerror = e => reject(e.target.error)
    reader['readAs' + type](file)
  })
}

const cardKeys = JSON.stringify([
  'source',
  'priority',
  'name',
  'home',
  'away',
  'round',
  'periods',
  'matchType',
  'startTime',
  'groupStage',
  'playOffStage',
])

@Component('sporty')
@Template(template)
@Attribute('season', Season)
@Attribute('source', SeasonSource)
@Attribute('max-sources', Number)
@Attribute('advanced-mode', Boolean)

export default class MatchSources extends HTMLElement {
  attached() {
    if(!this.uploadType) this.uploadType = 'lineups'
    this.maxSources = 0
    sourceManager.attached(this, 'match')
    this.update(Season.load({ active: true }))
      .then(() => (this.sport = this.season?.sport))

    this.reload()
  }

  uncache() {
    this.$cached = null
    this.render()
  }

  seasonChange() {
    this.reload()
  }

  changeSeason({season}) {
    query({season})
  }

  changeUploadType(type) {
    this.uploadType = type
    this.render()
  }

  setFile({files: [file]}) {
    const season = this.season._id
    this.render()
    this.$file = load(file, 'text')
    .then(data => {
        const result = {
          matches: [],
          lineups: [],
          events: [],
          oldGamesAllowed: true,
          type: this.uploadType,
          season,
      }
        function playerFromRow(result, row) {
          if(result.type === 'matches') {
            let [uid, startTime, name, round, matchType, teams] = row
              .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
              .map(thing => thing.trim())

            if(teams) teams = teams.replace(/"/g, ' ').split(',').map(t => t.trim())
            result.matches.push({ uid, startTime, name, round, matchType, teams })
          } else if(result.type === 'lineups') {
            let [player, team, match, status, matchStatus, duration, stats] = row
              .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
              .map(thing => thing.trim())
            if(stats) stats = JSON.parse(stats.replace(/"/g, ' ').replace(/\'/g, '"'))
            result.lineups.push({ player, team, match, status, matchStatus, duration, stats })
          } else if(result.type === 'events') {
            let [match, team, player, name, minute, second] = row
              .split(',')
              .map(thing => thing.trim())
            result.events.push({ match, team, player, name, minute, second})
          }
          return result
        }
        return data.trim().split('\n').reduce(playerFromRow, result)
      })
  }

  upload() {
    if(!this.$file) return null
    return this.$file
      .then(data => {
        return fetch('/api/player-sources/upload', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            authorization: `Bearer sportyman ${config.token}`,
            'Content-Type': 'application/json',
          },
        })
      })
      .then(() => {
        notify('Sources uploaded', {level: 'success', timeout: 5000})
        return this.reload()
      })
      .catch(err => {
        notify(err.message, {level: 'danger', timeout: 10000})
        console.error(err)
        this.render()
      })
  }



  async reload() {
    if(!this.season) return
    var params = {
      $reload: true,
      season: this.season._id,
    }
    await MatchSource.load(params).then(data => {
      this.realMatches = data.matches
      this.teams = data.teams
      this.matchSources = data.matchSources
      this.seasonSources = data.seasonSources
      this.customSources = data.customSources.map(s => {
        return MatchSource.instance(s._id, s)
      })
      sourceManager.cacheModels(this, 'match')
      this.$cached = null
      this.render()
    })
  }

  assignNew() {
    this.nonSources.forEach(source => {
      this.showScroll = true
      source.$new = true
    })
    this.render()
  }

  clear() {
    this.matchSources.forEach(source => {
      source.match = null
      this.showScroll = false
      delete source.$new
    })
    this.render()
  }

  get sortedTeams() {
    return (this.teams || [])
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .filter(a => !a.notAvailable)
  }

  get sortedMatches() {
    var maxSources = this.maxSources === (this.seasonSources || [].length)
      ? Infinity
      : this.maxSources
    return this.$cached || (this.$cached = sourceManager.sortedModels({
      attribute: 'match',
      search: this.search,
      sourceCount: maxSources,
      models: this.realMatches,
      sources: this.matchSources,
      sort: byTime,
      match(match, search) {
        return match.round == search || // Intentional double equals
          ~normalize(match.tag).indexOf(search) ||
          ~normalize(match.home).indexOf(search) ||
          ~normalize(match.away).indexOf(search) ||
          ~normalize(match.name).indexOf(search)
      },
    }))
  }

  customSource(match) {
    return (this.customByModel || {})[match]
  }

  overridedTeams(team) {
    if(team?.matchSources.find(s => s.source === 'custom')) return true
    return false
  }

  sources(match) {
    return (this.sourcesByModel || {})[match] || []
  }

  changeSport(sport) {
    this.sport = sport || null
    this.render()
  }

  updateFilter(e) {
    this.search = e.target.value
    this.$cached = null
    this.render()
  }

  save() {
    if(this.$isSubmitting) return
    this.$isSubmitting = true
    MatchSource.post('', {
      season: this.season._id,
      create: this.newMatches.map(s => s._id),
      unset: this.nonSources.filter(p => p.$changed).map(s => {
        s.$changed = false
        return s._id
      }),
      custom: this.customSources.filter(p => p.$changed).map(s => {
        s.$changed = false
        if(s.positions) {
          if(!s.positions.length) s.positions = undefined
        }
        return {
          match: s.matchId,
          round: s.round || '',
          bout: s.bout || '',
          leagueId: s.leagueId || '',
          seriesId: s.seriesId || '',
          startTime: s.startTime || '',
          endTime: s.endTime || '',
          teamOverrides: s.teamOverrides || [],
          uid: s.uid || '',
          source: s.source || '',
          difficulty: s.difficulty,
          matchType: s.matchType,
          distance: s.distance,
          finishLine: s.finishLine,
          name: s.name || '',
          periods: s.periods,
          locations: s.locations || [],
          tag: s.tag || '',
          groupStage: s.groupStage || '',
          playOffStage: s.playOffStage || '',
          customDifficulty: s.customDifficulty || [],
          positions: s.positions,
          liveStreamUrl: s.liveStreamUrl || '',
          externalMatchPageUrl: s.externalMatchPageUrl || '',
          liveStreamSource: s.liveStreamSource || '',
          score: s.score || [0, 0],
          isSubMatch: s.isSubMatch || false,
          isSingleRound: s.isSingleRound || false,
          points: s.points || 0,
        }
      }),
      pair: this.matchSources
        .filter(s => s.$changed && s.match)
        .map(s => {
          s.$changed = false
          return {
            _id: s._id,
            match: s.matchId,
            priority: s.priority,
          }
        }),
    }).then(() => {
        notify('Sources updated', {level: 'success', timeout: 5000})
        return this.reload()
      })
      .catch(err => {
        notify(err.message, {level: 'danger', timeout: 10000})
        console.error(err)
        this.render()
      })
      .then(() => {
        this.$isSubmitting = false
      })
    this.render()
  }
  autoAssign() {
    sourceManager.autoAssign({
      attribute: 'match',
      sources: this.matchSources,
      models: this.realMatches,
      seasonSource: this.source,
      hash: customToKey,
    })
    this.uncache()
  }
  addOverride(match, teams) {
    const source = this.customSource(match)
    if(!source) {
      return
    }
    for(const team of teams) {
      source.addOverride(team)
    }
    source.$changed = true
    this.render()
  }

  async openModal(openMatch) {
    const edit = EditMatchSource({ season: this.season, match: openMatch })
    const { match, teams, source } = await Modal(
      edit,
      { hideCloseBtn: true },
    )
    if(match) {
      if(!this.realMatches.includes(match)) {
        this.realMatches.push(match)
        this.$cached = null
      }
      if(source) {
        this.customSources.push(source)
        this.customByModel[source.match] = source
        this.addOverride(match, teams)
      }

      await match.load()
      this.render()
    } else {
      await this.reload()
    }
  }

  get cardKeys() {
    return cardKeys
  }

  get customFields() {
    return customFields(this.season?.rules)
  }

  get warnings() {
    const ret = []
    if((this.realMatches || []).find(m => m.round == null)) {
      ret.push('Unassigned round. Fixture feed will crash. Search for "GWundefined."')
    }
    return ret
  }

  get newMatches() {
    return (this.matchSources || [])
      .filter(s => !s.match && s.$new)
      .sort(byTime)
  }

  get nonSources() {
    return sourceManager.nonSources({
      attribute: 'match',
      sources: this.matchSources,
      seasonSource: this.source,
    }).sort(byTime)
  }
  get activeSeasons() {
    const active = this.seasons.filter((s) => s.active)
    return Array.from(new Set(active.map(season => season.sport))).sort()
  }
}
